import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '@env/environment';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';

export type LandingSolutionMediaKey =
    | 'live-asset-tracking'
    | 'cold-chain-monitoring'
    | 'people-and-safety'
    | 'insight-suite'
    | 'predictive-maintenance'
    | 'yard-management'
    | 'compliance-and-audit';

export type LandingFeatureMediaKey =
    | 'ai'
    | 'product-discovery'
    | 'found-asset-kiosk'
    | 'predictive'
    | 'live-map'
    | 'zone-analytics'
    | 'alerts'
    | 'geo-fencing'
    | 'sensor-telemetry'
    | 'reports'
    | 'playback'
    | 'map-designer'
    | 'location-structure'
    | 'tag-mgmt'
    | 'batch-import'
    | 'access-rules'
    | 'rbac'
    | 'settings'
    | 'multilingual';

export interface LandingMediaReference {
    videoUrl?: string;
    coverUrl?: string;
}

export interface LandingMediaManifest {
    solutions: Record<string, LandingMediaReference>;
    features: Record<string, LandingMediaReference>;
}

const emptyManifest: LandingMediaManifest = {
    solutions: {},
    features: {}
};

export const LANDING_SOLUTION_MEDIA_FALLBACKS: Record<LandingSolutionMediaKey, LandingMediaReference> = {
    'live-asset-tracking': {
        videoUrl: 'assets/landing/solution-videos/live-asset-tracking.mp4',
        coverUrl: 'assets/landing/solution-photos/live-asset-tracking.jpg'
    },
    'cold-chain-monitoring': {
        videoUrl: 'assets/landing/solution-videos/cold-chain-monitoring.mp4',
        coverUrl: 'assets/landing/solution-photos/cold-chain-monitoring.jpg'
    },
    'people-and-safety': {
        videoUrl: 'assets/landing/solution-videos/people-and-safety.mp4',
        coverUrl: 'assets/landing/solution-photos/people-and-safety.jpg'
    },
    'insight-suite': {
        videoUrl: 'assets/landing/solution-videos/insight-suite.mp4',
        coverUrl: 'assets/landing/solution-photos/insight-suite.jpg'
    },
    'predictive-maintenance': {
        videoUrl: 'assets/landing/solution-videos/predictive-maintenance.mp4',
        coverUrl: 'assets/landing/solution-photos/predictive-maintenance.jpg'
    },
    'yard-management': {
        videoUrl: 'assets/landing/solution-videos/yard-management.mp4',
        coverUrl: 'assets/landing/solution-photos/yard-management.jpg'
    },
    'compliance-and-audit': {
        videoUrl: 'assets/landing/solution-videos/compliance-and-audit.mp4',
        coverUrl: 'assets/landing/solution-photos/compliance-and-audit.jpg'
    }
};

export const LANDING_FEATURE_MEDIA_FALLBACKS: Record<LandingFeatureMediaKey, LandingMediaReference> = {
    'ai': {
        videoUrl: 'assets/landing/feature-videos/ai.mp4',
        coverUrl: 'assets/landing/feature-covers/ai.jpg'
    },
    'product-discovery': {
        videoUrl: 'assets/landing/feature-videos/product-discovery.mp4',
        coverUrl: 'assets/landing/feature-covers/product-discovery.jpg'
    },
    'found-asset-kiosk': {
        videoUrl: 'assets/landing/feature-videos/found-asset-kiosk.mp4',
        coverUrl: 'assets/landing/feature-covers/found-asset-kiosk.jpg'
    },
    'predictive': {
        videoUrl: 'assets/landing/feature-videos/predictive.mp4',
        coverUrl: 'assets/landing/feature-covers/predictive.jpg'
    },
    'live-map': {
        videoUrl: 'assets/landing/feature-videos/live-map.mp4',
        coverUrl: 'assets/landing/feature-covers/live-map.jpg'
    },
    'zone-analytics': {
        videoUrl: 'assets/landing/feature-videos/zone-analytics.mp4',
        coverUrl: 'assets/landing/feature-covers/zone-analytics.jpg'
    },
    'alerts': {
        videoUrl: 'assets/landing/feature-videos/alerts.mp4',
        coverUrl: 'assets/landing/feature-covers/alerts.jpg'
    },
    'geo-fencing': {
        videoUrl: 'assets/landing/feature-videos/geo-fencing.mp4',
        coverUrl: 'assets/landing/feature-covers/geo-fencing.jpg'
    },
    'sensor-telemetry': {
        videoUrl: 'assets/landing/feature-videos/sensor-telemetry.mp4',
        coverUrl: 'assets/landing/feature-covers/sensor-telemetry.jpg'
    },
    'reports': {
        videoUrl: 'assets/landing/feature-videos/reports.mp4',
        coverUrl: 'assets/landing/feature-covers/reports.jpg'
    },
    'playback': {
        videoUrl: 'assets/landing/feature-videos/playback.mp4',
        coverUrl: 'assets/landing/feature-covers/playback.jpg'
    },
    'map-designer': {
        videoUrl: 'assets/landing/feature-videos/map-designer.mp4',
        coverUrl: 'assets/landing/feature-covers/map-designer.jpg'
    },
    'location-structure': {
        videoUrl: 'assets/landing/feature-videos/location-structure.mp4',
        coverUrl: 'assets/landing/feature-covers/location-structure.jpg'
    },
    'tag-mgmt': {
        videoUrl: 'assets/landing/feature-videos/tag-mgmt.mp4',
        coverUrl: 'assets/landing/feature-covers/tag-mgmt.jpg'
    },
    'batch-import': {
        videoUrl: 'assets/landing/feature-videos/batch-import.mp4',
        coverUrl: 'assets/landing/feature-covers/batch-import.jpg'
    },
    'access-rules': {
        videoUrl: 'assets/landing/feature-videos/access-rules.mp4',
        coverUrl: 'assets/landing/feature-covers/access-rules.jpg'
    },
    'rbac': {
        videoUrl: 'assets/landing/feature-videos/rbac.mp4',
        coverUrl: 'assets/landing/feature-covers/rbac.jpg'
    },
    'settings': {
        videoUrl: 'assets/landing/feature-videos/settings.mp4',
        coverUrl: 'assets/landing/feature-covers/settings.jpg'
    },
    'multilingual': {
        videoUrl: 'assets/landing/feature-videos/multilingual.mp4',
        coverUrl: 'assets/landing/feature-covers/multilingual.jpg'
    }
};

@Injectable({ providedIn: 'root' })
export class LandingMediaService {
    private readonly manifestUrl = '/assets/landing/media-manifest.json';
    private readonly manifest$: Observable<LandingMediaManifest>;

    constructor(private readonly http: HttpClient) {
        this.manifest$ = environment.production
            ? this.http.get<LandingMediaManifest>(this.manifestUrl).pipe(
                map((manifest) => this.normalizeManifest(manifest)),
                catchError(() => of(emptyManifest)),
                shareReplay({ bufferSize: 1, refCount: true })
            )
            : of(emptyManifest);
    }

    getManifest(): Observable<LandingMediaManifest> {
        return this.manifest$;
    }

    resolveSolutionMedia(
        mediaKey: LandingSolutionMediaKey,
        manifest: LandingMediaManifest
    ): LandingMediaReference {
        return this.resolveReference(manifest.solutions[mediaKey], LANDING_SOLUTION_MEDIA_FALLBACKS[mediaKey]);
    }

    resolveFeatureMedia(
        mediaKey: LandingFeatureMediaKey,
        manifest: LandingMediaManifest
    ): LandingMediaReference {
        return this.resolveReference(manifest.features[mediaKey], LANDING_FEATURE_MEDIA_FALLBACKS[mediaKey]);
    }

    private normalizeManifest(manifest: LandingMediaManifest | null | undefined): LandingMediaManifest {
        return {
            solutions: manifest?.solutions ?? {},
            features: manifest?.features ?? {}
        };
    }

    private resolveReference(
        candidate: LandingMediaReference | null | undefined,
        fallback: LandingMediaReference
    ): LandingMediaReference {
        return {
            videoUrl: candidate?.videoUrl || fallback.videoUrl,
            coverUrl: candidate?.coverUrl || fallback.coverUrl
        };
    }
}
